import React, { useEffect, useState } from 'react';
import { useAuthStore } from '../store/useAuthStore';
import { usePersistedAuthStore } from '../store/usePersistedAuthStore';
import { useUserStore } from '../store/useUserStore';
import { useTenantStore } from '../store/useTenantStore';
import { apiClient } from '../lib/api/client';
import { ApiResponse, SessionInfo, LoginResponse } from '../lib/api/types';
import { useWebViewContext, useWebViewAuth } from '../integrations/webview';

interface AuthWrapperProps {
  children: React.ReactNode;
}

export const AuthWrapper: React.FC<AuthWrapperProps> = ({ children }) => {
  const [initializing, setInitializing] = useState(true);
  // const { accessToken, isTokenExpired, setAccessToken } = useAuthStore();
  // const { refreshToken, setRefreshToken, userId } = usePersistedAuthStore();
  const setUserInfo = useUserStore((state) => state.setUserInfo);
  
  // WebView integration
  const { isWebView, externalProps, isLoading: webViewLoading } = useWebViewContext();
  const { isAuthenticated: webViewAuthenticated, isAuthenticating } = useWebViewAuth();

  useEffect(() => {
    const initAuth = async () => {
      // Wait for WebView context to load
      if (webViewLoading) {
        return;
      }
      
      // If in WebView with external props, skip normal auth flow
      if (isWebView && externalProps) {
        console.log('AuthWrapper - WebView mode detected with external props, skipping auth');
        setInitializing(false);
        return;
      }
      
      // If in WebView and authenticated, skip normal auth flow
      if (isWebView && webViewAuthenticated) {
        setInitializing(false);
        return;
      }
      
      // If in WebView but still authenticating, wait
      if (isWebView && isAuthenticating) {
        return;
      }
      
      // Normal auth flow for non-WebView
      if (!isWebView) {
        // Wait for zustand to rehydrate from localStorage
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      const currentAuthState = useAuthStore.getState();
      const currentPersistedState = usePersistedAuthStore.getState();
   
      
      // Check if we have tenant info before making any API calls
      const currentTenantState = useTenantStore.getState();
      const hasTenant = currentTenantState.hasValidTenant();
      
      // Check if we have a valid access token
      if (currentAuthState.accessToken && !currentAuthState.isTokenExpired()) {
        
        // Only fetch user info if we have both tenant info and userId
        if (hasTenant && currentPersistedState.userId && !useUserStore.getState().userInfo) {
          try {
            const userInfoResponse = await apiClient.getCurrentLoginInfo();
            const userInfoData = userInfoResponse.data as ApiResponse<SessionInfo>;

            if (userInfoData.success && userInfoData.result?.user) {
              setUserInfo({
                id: userInfoData.result.user.id,
                userName: userInfoData.result.user.userName,
                name: userInfoData.result.user.name,
                surname: userInfoData.result.user.surname,
                emailAddress: userInfoData.result.user.emailAddress,
                isActive: userInfoData.result.user.isActive || true,
                fullName: `${userInfoData.result.user.name} ${userInfoData.result.user.surname}`.trim(),
                lastLoginTime: userInfoData.result.user.lastLoginTime ? new Date(userInfoData.result.user.lastLoginTime) : null,
                creationTime: userInfoData.result.user.creationTime ? new Date(userInfoData.result.user.creationTime) : new Date(),
                roleNames: userInfoData.result.user.roleNames || [],
              });
            }
          } catch (error) {
            // Silently ignore - user will need to login again
          }
        }
        
        setInitializing(false);
        return;
      }

      // No valid access token, check refresh token
      if (!currentPersistedState.refreshToken || !hasTenant) {
        setInitializing(false);
        return;
      }

      try {
        // Try to refresh token - this will use the correct tenant URL
        const response = await apiClient.refreshTokenRequest(currentPersistedState.refreshToken);

        const data = response.data as ApiResponse<LoginResponse>;

        if (data.success && data.result) {
          const { accessToken, expireInSeconds, refreshToken: newRefreshToken } = data.result;
          
          // Update tokens
          currentAuthState.setAccessToken(accessToken, expireInSeconds);
          currentPersistedState.setRefreshToken(newRefreshToken);

          // Get user info only if we have tenant
          if (hasTenant) {
            const userInfoResponse = await apiClient.getCurrentLoginInfo();
            const userInfoData = userInfoResponse.data as ApiResponse<SessionInfo>;

            if (userInfoData.success && userInfoData.result?.user) {
              setUserInfo({
                id: userInfoData.result.user.id,
                userName: userInfoData.result.user.userName,
                name: userInfoData.result.user.name,
                surname: userInfoData.result.user.surname,
                emailAddress: userInfoData.result.user.emailAddress,
                isActive: userInfoData.result.user.isActive || true,
                fullName: `${userInfoData.result.user.name} ${userInfoData.result.user.surname}`.trim(),
                lastLoginTime: userInfoData.result.user.lastLoginTime ? new Date(userInfoData.result.user.lastLoginTime) : null,
                creationTime: userInfoData.result.user.creationTime ? new Date(userInfoData.result.user.creationTime) : new Date(),
                roleNames: userInfoData.result.user.roleNames || [],
              });
            }
          }
        }
      } catch (error) {
        // Clear all auth data on error
        currentAuthState.clearAuth();
        currentPersistedState.clearPersistedAuth();
        useUserStore.getState().clearUserInfo();
      } finally {
        setInitializing(false);
      }
    };

    initAuth();
  }, [webViewLoading, isWebView, webViewAuthenticated, isAuthenticating, externalProps]);

  if (initializing || webViewLoading || (isWebView && isAuthenticating)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return <>{children}</>;
};